import React from 'react'
import { Column } from '../types/table'
import { money, number, percentage } from './formatters'
import { FilterI } from '../constants/filter'
import { Filters, KDPPerformanceFilters } from '../config'
import { BlendedAcosCell } from '../components/table/AcosCell'
import { BlendedRoasCell } from '../components/table/RoasCell'
import { TooltipHeader } from '../components/table/TooltipHeader'
import { Calculation, CalculationLine } from '../components/table/Calculation'
import { BlendedRpcCell } from '../components/table/RpcCell'
import { BlendedCvrCell } from '../components/table/CvrCell'
import { BlendedCacCell } from '../components/table/CacCell'
import { BlendedAovCell } from '../components/table/AovCell'

export const scaledImpactMultiplier = (value?: number | null, fallback = 1.0) =>
    value == null ? fallback : value / 100.0

const AdjustedOrders: Column<any, any> = {
    key: 'adjusted_orders',
    title: TooltipHeader('Adj Orders', 'Adjusted Orders', 'orders * sales impact multiplier')(),
    formatter: (v: any) => number(v ?? 0),
    width: 100,
    cell: (v: any, extra) => {
        const suffix = getSuffix(extra.col?.key)
        const g = (key: string) => get(v, key, suffix)

        const lines: CalculationLine[] = [
            { description: 'Orders', value: number(g('orders')) ?? '' },
            {
                description: '* Sales Impact Multiplier',
                value:
                    number(
                        scaledImpactMultiplier(extra?.impact_multiplier?.orders_impact_multiplier),
                        2,
                    ) ?? '',
            },
            { description: 'Adjusted Orders', value: number(g('adjusted_orders')) ?? '' },
        ]
        return <Calculation lines={lines}>{number(g('adjusted_orders'))}</Calculation>
    },
}

const AdjustedSales: Column<any, any> = {
    key: 'adjusted_sales',
    title: TooltipHeader('Adj Sales', 'Adjusted Sales', 'sales * sales impact multiplier')(),
    formatter: (v: any) => money(v ?? 0),
    width: 100,
    cell: (v: any, extra) => {
        const suffix = getSuffix(extra.col?.key)
        const g = (key: string) => get(v, key, suffix)

        const lines: CalculationLine[] = [
            { description: 'Sales', value: money(g('sales')) ?? '' },
            {
                description: '* Sales Impact Multiplier',
                value:
                    number(
                        scaledImpactMultiplier(extra?.impact_multiplier?.orders_impact_multiplier),
                        2,
                    ) ?? '',
            },
            { description: 'Adjusted Sales', value: money(g('adjusted_sales')) ?? '' },
        ]
        return <Calculation lines={lines}>{money(g('adjusted_sales'))}</Calculation>
    },
}

const BlendedProfit: Column<any, any> = {
    key: 'blended_profit',
    title: TooltipHeader(
        'Blended Profit',
        'Blended Profit',
        'sales * sales impact multiplier * profit margin + estimated royalties * KENP impact multiplier',
    )(),
    formatter: (v: any) => money(v ?? 0),
    width: 100,
    cell: (v: any, extra) => {
        const suffix = getSuffix(extra.col?.key)
        const g = (key: string) => get(v, key, suffix)

        const lines: CalculationLine[] = [
            { description: '(Sales', value: money(g('sales')) ?? '' },
            {
                description: '* Sales Impact Multiplier',
                value:
                    number(
                        scaledImpactMultiplier(extra?.impact_multiplier?.orders_impact_multiplier),
                        2,
                    ) ?? '',
            },
            {
                description: '* Profit Margin)',
                value:
                    percentage(
                        scaledImpactMultiplier(extra?.impact_multiplier?.profit_margin, 100.0),
                    ) ?? '',
            },
            {
                description: '+ (Estimated Royalties',
                value: money(get(v, 'estimated_royalties', suffix)) ?? '',
            },
            {
                description: '* KENP Impact Multiplier)',
                value:
                    number(
                        scaledImpactMultiplier(
                            extra?.impact_multiplier?.kenp_read_impact_multiplier,
                        ),
                        2,
                    ) ?? '',
            },
            { description: 'Blended Profit', value: money(g('blended_profit')) ?? '' },
        ]
        return <Calculation lines={lines}>{money(g('blended_profit'))}</Calculation>
    },
}

const BlendedACOS: Column<any, any> = {
    key: 'blended_acos',
    title: TooltipHeader(
        'Blended ACOS',
        'Blended Advertising Cost of Sale',
        'spend / blended sales',
    )(),
    formatter: (v: any) => percentage(v ?? 0),
    width: 100,
    cell: (v: any, extra) => {
        const suffix = getSuffix(extra.col?.key)
        const g = (key: string) => get(v, key, suffix)
        let acos = g('blended_acos')
        let target_acos = v.target_acos
        if (extra?.recipe_version === 2) {
            acos = acos * 100.0
            target_acos *= 10000.0
        }
        return BlendedAcosCell({
            ...v,
            target_acos,
            cost: g('cost'),
            acos,
        })
    },
}

const BlendedCVR: Column<any, any> = {
    key: 'blended_cvr',
    title: TooltipHeader('Blended CVR', 'Blended Conversion Rate', 'adjusted orders / clicks')(),
    formatter: (v: any) => percentage(v ?? 0),
    width: 100,
    cell: (v: any, extra) => {
        const suffix = getSuffix(extra.col?.key)
        const g = (key: string) => get(v, key, suffix)

        return BlendedCvrCell(
            {
                clicks: g('clicks'),
                adjusted_orders: g('adjusted_orders'),
                blended_cvr: g('blended_cvr'),
            },
            extra,
        )
    },
}

const BlendedCAC: Column<any, any> = {
    key: 'blended_cac',
    title: TooltipHeader(
        'Blended CAC',
        'Blended Customer Acquisition Cost',
        'spend / adjusted orders',
    )(),
    formatter: (v: any) => money(v ?? 0),
    width: 100,
    cell: (v: any, extra) => {
        const suffix = getSuffix(extra.col?.key)
        const g = (key: string) => get(v, key, suffix)

        return BlendedCacCell({
            blended_cac: g('blended_cac'),
            adjusted_orders: g('adjusted_orders'),
            cost: g('cost'),
        })
    },
}

const BlendedROAS: Column<any, any> = {
    key: 'blended_roas',
    title: TooltipHeader(
        'Blended ROAS',
        'Blended Return on Advertising Spend',
        'blended sales / spend',
    )(),
    formatter: (v: any) => percentage(v ?? 0),
    width: 100,
    cell: (v: any, extra) => {
        const suffix = getSuffix(extra.col?.key)
        const g = (key: string) => get(v, key, suffix)
        let roas = g('blended_roas')
        let acos = g('blended_acos')
        let target_acos = v.target_acos
        if (extra?.recipe_version === 2) {
            acos = acos * 100.0
            target_acos *= 10000.0
        }

        return BlendedRoasCell({
            ...v,
            acos,
            target_acos,
            roas,
            cost: g('cost'),
        })
    },
}

const BlendedAOV: Column<any, any> = {
    key: 'blended_aov',
    title: TooltipHeader(
        'Blended AOV',
        'Blended Average Order Value',
        'blended sales / adjusted orders',
    )(),
    formatter: (v: any) => money(v ?? 0),
    width: 100,
    cell: (v: any, extra) => {
        const suffix = getSuffix(extra.col?.key)
        const g = (key: string) => get(v, key, suffix)

        return BlendedAovCell({
            blended_aov: g('blended_aov'),
            adjusted_orders: g('adjusted_orders'),
            blended_profit: g('blended_profit'),
        })
    },
}

const BlendedRPC: Column<any, any> = {
    key: 'blended_rpc',
    title: TooltipHeader('Blended RPC', 'Blended Revenue per Click', 'blended sales / clicks')(),
    formatter: (v: any) => money(v ?? 0),
    width: 100,
    cell: (v: any, extra) => {
        const suffix = getSuffix(extra.col?.key)
        const g = (key: string) => get(v, key, suffix)

        return BlendedRpcCell({
            blended_rpc: g('blended_rpc'),
            clicks: g('clicks'),
            blended_profit: g('blended_profit'),
        })
    },
}

const AdjustedPagesRead: Column<any, any> = {
    key: 'adjusted_pages_read',
    title: TooltipHeader(
        'Adj Pages Read',
        'Adjusted Pages Read',
        'pages read * KENP impact multiplier',
    )(),
    formatter: (v: any) => number(v ?? 0),
    cell: (v: any, extra) => {
        const suffix = getSuffix(extra.col?.key)
        const g = (key: string) => get(v, key, suffix)

        const lines: CalculationLine[] = [
            { description: 'Pages Read', value: number(g('pages_read')) ?? '' },
            {
                description: '* KENP Read Impact Multiplier',
                value:
                    number(
                        scaledImpactMultiplier(
                            extra?.impact_multiplier?.kenp_read_impact_multiplier,
                        ),
                        2,
                    ) ?? '',
            },
            { description: 'Adjusted Pages Read', value: number(g('adjusted_pages_read')) ?? '' },
        ]
        return <Calculation lines={lines}>{number(g('adjusted_pages_read'))}</Calculation>
    },
}

const AdjustedRoyalties: Column<any, any> = {
    key: 'adjusted_estimated_royalties',
    title: TooltipHeader(
        'Adj ESt Royalties',
        'Adjusted Estimated Royalties',
        'estimated royalties * KENP impact multiplier',
    )(),
    formatter: (v: any) => money(v ?? 0),
    cell: (v: any, extra) => {
        const suffix = getSuffix(extra.col?.key)
        const g = (key: string) => get(v, key, suffix)

        const read_multiplier = extra?.impact_multiplier?.kenp_read_impact_multiplier as
            | number
            | null
        const lines: CalculationLine[] = [
            { description: 'Estimated Royalties', value: money(g('estimated_royalties')) ?? '' },
            {
                description: '* KENP Read Impact Multiplier',
                value: number(read_multiplier ? read_multiplier / 100.0 : 1.0, 2),
            },
            {
                description: 'Adjusted Estimated Royalties',
                value: money(g('adjusted_estimated_royalties')) ?? '',
            },
        ]
        return <Calculation lines={lines}>{money(g('adjusted_estimated_royalties'))}</Calculation>
    },
}

interface hasKey {
    key: string
}

export const add_kdp_cols = <T, Extra extends object>(columns: Column<T, Extra>[]) => {
    // Add in adjusted columns
    spliceAfter('orders', columns, AdjustedOrders)
    spliceAfter('sales', columns, AdjustedSales)
    spliceAfter('adjusted_sales', columns, BlendedProfit)
    spliceAfter('acos', columns, BlendedACOS)
    spliceAfter('roas', columns, BlendedROAS)
    spliceAfter('cvr', columns, BlendedCVR)
    spliceAfter('cac', columns, BlendedCAC)
    spliceAfter('aov', columns, BlendedAOV)
    spliceAfter('rpc', columns, BlendedRPC)
    spliceAfter('pages_read', columns, AdjustedPagesRead)
    spliceAfter('estimated_royalties', columns, AdjustedRoyalties)
}

const spliceAfter = <T extends hasKey>(key: string, columns: T[], col: T) => {
    const index = columns.findIndex((col) => col.key === key)
    if (index == -1) {
        console.error(`spliceAfter: key ${key} not found in columns`)
        return
    }
    columns.splice(index + 1, 0, col)
}

export const add_kdp_filters = (filters: FilterI[]) => {
    for (const f of KDPPerformanceFilters) {
        filters.push(f)
    }

    spliceAfter('orders', filters, {
        ...Filters.Orders,
        key: 'adjusted_orders',
        name: 'Adjusted Orders',
    })

    spliceAfter('sales', filters, {
        ...Filters.Sales,
        key: 'adjusted_sales',
        name: 'Adjusted Sales',
    })

    spliceAfter('adjusted_sales', filters, {
        ...Filters.Sales,
        key: 'blended_profit',
        name: 'Blended Profit',
    })

    spliceAfter('acos', filters, {
        ...Filters.ACOS,
        key: 'blended_acos',
        name: 'Blended ACOS',
    })

    spliceAfter('roas', filters, {
        ...Filters.ROAS,
        key: 'blended_roas',
        name: 'Blended ROAS',
    })

    spliceAfter('cvr', filters, {
        ...Filters.CVR,
        key: 'blended_cvr',
        name: 'Blended CVR',
    })

    spliceAfter('cac', filters, {
        ...Filters.CAC,
        key: 'blended_cac',
        name: 'Blended CAC',
    })

    spliceAfter('aov', filters, {
        ...Filters.AOV,
        key: 'blended_aov',
        name: 'Blended AOV',
    })

    spliceAfter('rpc', filters, {
        ...Filters.RPC,
        key: 'blended_rpc',
        name: 'Blended RPC',
    })

    spliceAfter('pages_read', filters, {
        ...Filters.PagesRead,
        key: 'adjusted_pages_read',
        name: 'Adjusted Pages Read',
    })

    spliceAfter('estimated_royalties', filters, {
        ...Filters.EstimatedRoyalties,
        key: 'estimated_royalties',
        name: 'Adjusted Estimated Royalties',
    })

    return [...filters]
}

export const getSuffix = (key?: string): string | null => {
    if (!key) {
        return null
    }

    // Every form of TimePeriod::human_name in the recipes DSL: `30d`, the offset
    // range `14d__29d` (either end may be empty), `<start>_to_<end>`, `lifetime`.
    const match = key.match(
        /_(?:\d+d|(?:\d+d)?__(?:\d+d)?|(?:\d{4}-\d\d-\d\d)?_to_(?:\d{4}-\d\d-\d\d)?|lifetime)$/,
    )
    return match ? match[0] : null
}

export const get = (v: any, key: string, suffix: string | null = null): any => {
    return v[key + (suffix ?? '')]
}
